import React, { useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBTable,
  MDBTableBody,
  MDBTableHead,
  MDBIcon,
  MDBInput,
  MDBPagination,
  MDBPageItem,
  MDBPageNav,
  MDBCollapse,
} from 'mdbreact';

import NavBar from '../../../components/NavBar';
import api from '../../../services/api';
import {
  FILTROS_VAZIOS, filtrosDaQuery, paramsDaApi, resultadoDaBusca, navegacaoPara, avisoDeCorte,
  filtrosForaDoFormulario, paginaCorrigida, soDigitos,
} from './filtrosAutos';

const BADGE_STATUS_AI = {
  Liquidado:   { bg: '#2e7d32', text: '#fff' },
  Retificado:  { bg: '#e65100', text: '#fff' },
  Lavrado:     { bg: '#1565c0', text: '#fff' },
};

const BADGE_ACOMPANHAMENTO = {
  'Em Dívida Ativa': { bg: '#b71c1c', text: '#fff' },
  Extinto:           { bg: '#424242', text: '#fff' },
  Liquidado:         { bg: '#2e7d32', text: '#fff' },
};

const BadgeStatus = ({ valor, tipo }) => {
  if (!valor) return <span style={{ fontSize: '11px', color: '#999' }}>-</span>;
  const mapa = tipo === 'acompanhamento' ? BADGE_ACOMPANHAMENTO : BADGE_STATUS_AI;
  const chave = Object.keys(mapa).find(k => valor.includes(k));
  const cores = chave ? mapa[chave] : { bg: '#616161', text: '#fff' };
  return (
    <span
      className="badge p-1 px-2"
      style={{
        fontSize: '11px',
        fontWeight: 500,
        backgroundColor: cores.bg,
        color: cores.text,
        borderRadius: '4px',
        whiteSpace: 'nowrap',
      }}
    >
      {valor}
    </span>
  );
};

const StatCard = ({ icon, label, value, gradient }) => (
  <MDBCol md="4" sm="6" className="mb-3">
    <div
      style={{
        background: gradient,
        borderRadius: '10px',
        padding: '14px 16px',
        color: '#fff',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
      }}
    >
      <div className="d-flex align-items-center justify-content-between">
        <div style={{ minWidth: 0 }}>
          <p className="mb-0" style={{ fontSize: '11px', opacity: 0.85 }}>{label}</p>
          <h5
            className="mb-0 font-weight-bold"
            style={{ fontSize: '14px', wordBreak: 'break-word' }}
          >
            {value}
          </h5>
        </div>
        <MDBIcon icon={icon} size="lg" style={{ opacity: 0.3, marginLeft: '8px', flexShrink: 0 }} />
      </div>
    </div>
  </MDBCol>
);

export default function ListarAutosInfracao() {
  const [loading, setLoading] = useState(false);
  // Lista, totais, cards e erro mudam juntos — ver resultadoDaBusca.
  const [busca, setBusca] = useState({
    registros: [], totalRegistros: 0, totalPaginas: 0, estatisticas: null, erro: null,
  });
  const { registros, totalRegistros, totalPaginas, estatisticas, erro: erroBusca } = busca;
  const [itensPorPagina] = useState(20);

  const history = useHistory();
  const location = useLocation();
  // Filtros APLICADOS vêm da URL; `rascunho` é o que está sendo digitado no
  // formulário e só vira URL no "Buscar".
  const { filtros, pagina: paginaAtual } = filtrosDaQuery(location.search);
  const [rascunho, setRascunho] = useState(filtros);
  const [opcoes, setOpcoes] = useState({ status_ai: [], status_acompanhamento: [], postos: [] });

  // Link que já chega filtrado (do painel, ou compartilhado) abre os filtros.
  const [mostrarFiltros, setMostrarFiltros] = useState(
    Object.keys(paramsDaApi(filtros)).length > 0,
  );

  const aplicar = (novos, pagina = 1, opcoesNav) => {
    const { metodo, search, refazer } = navegacaoPara(location.search, novos, pagina, opcoesNav);
    history[metodo]({ pathname: location.pathname, search, state: refazer ? { t: Date.now() } : undefined });
  };
  const setCampo = (k) => (e) => setRascunho({ ...rascunho, [k]: e.target.value });
  // Nº AI colado com pontos ("36.169.126"): o type="number" virava vazio e a busca
  // ignorava o filtro sem avisar (#394). Texto numérico, só com os dígitos.
  const setNumero = (k) => (e) => setRascunho({ ...rascunho, [k]: soDigitos(e.target.value) });
  const foraDoFormulario = filtrosForaDoFormulario(filtros, opcoes);
  const irParaPagina = (n) => aplicar(filtros, n);

  useEffect(() => {
    let vivo = true;
    api.get('/auto-infracao-receita/opcoes')
      .then((r) => { if (vivo) setOpcoes(r.data); })
      .catch(() => { if (vivo) toast.error('Não foi possível carregar as opções de filtro.'); });
    return () => { vivo = false; };
  }, []);

  useEffect(() => {
    // URL mudou (Buscar, Limpar, paginação, link do painel, voltar do navegador):
    // o formulário acompanha e as duas buscas usam os mesmos filtros.
    setRascunho(filtros);
    const params = paramsDaApi(filtros);
    let vivo = true;
    setLoading(true);
    Promise.all([
      api.get('/auto-infracao-receita', { params: { ...params, page: paginaAtual, limit: itensPorPagina } }),
      api.get('/auto-infracao-receita/estatisticas', { params }),
    ])
      .then(([lista, stats]) => {
        if (!vivo) return;
        // Link velho para página que não existe mais (#394): vai para a última.
        const ultima = paginaCorrigida(paginaAtual, lista.data.total_paginas);
        if (ultima) {
          // replace: o Voltar não pode cair de novo na página inexistente.
          // `vivo = false` segura o setLoading(false) do finally — a busca da
          // página certa já vai começar, e a tela não pisca "Nenhum registro".
          vivo = false;
          aplicar(filtros, ultima, { substituir: true });
          return;
        }
        setBusca(resultadoDaBusca({ lista, stats }));
      })
      .catch((error) => {
        if (!vivo) return;
        const r = resultadoDaBusca({ erro: error });
        setBusca(r);
        toast.error(r.erro);
      })
      .finally(() => vivo && setLoading(false));
    return () => { vivo = false; };
    // location.search/state são a dependência real; `filtros`/`paginaAtual` derivam deles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.state]);

  const formatarValor = (valor) => {
    if (!valor) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
  };

  const formatarData = (data) => {
    if (!data) return '-';
    try {
      return format(parseISO(data), 'dd/MM/yyyy');
    } catch (e) {
      return '-';
    }
  };

  const exportarCSV = async () => {
    try {
      toast.info('Preparando exportação...');
      const params = paramsDaApi(filtros);

      const response = await api.get('/auto-infracao-receita', {
        params: { ...params, limit: 10000 },
      });

      if (!response.data.registros || response.data.registros.length === 0) {
        toast.warning('Nenhum registro para exportar');
        return;
      }

      const csvData = response.data.registros.map(r => ({
        NU_AI: r.nu_ai,
        DT_LAVRATURA: formatarData(r.dt_lavratura),
        STATUS_AI: r.status_ai,
        STATUS_ACOMPANHAMENTO: r.status_acompanhamento,
        NU_INFRACAO: r.nu_infracao,
        INFRACAO: r.infracao,
        RF_LAVRATURA: r.rf_lavratura,
        IMPOSTO: r.imposto,
        MULTA: r.multa,
        MULTA_FORMAL: r.multa_formal,
        TOTAL: r.total,
      }));

      const csvContent = [
        Object.keys(csvData[0]).join(';'),
        ...csvData.map(row => Object.values(row).join(';')),
      ].join('\n');

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.setAttribute('href', URL.createObjectURL(blob));
      link.setAttribute('download', `autos_infracao_${new Date().getTime()}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      const corte = avisoDeCorte(response.data.total, response.data.registros.length);
      if (corte) toast.warning(corte, { autoClose: false });
      else toast.success('Arquivo exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.error('Erro ao exportar dados');
    }
  };

  return (
    <>
      <NavBar />
      <MDBContainer className="mt-4">
        {/* Header */}
        <MDBRow className="mb-3">
          <MDBCol md="12">
            <h4 className="font-weight-bold" style={{ color: '#2c3e50' }}>
              <MDBIcon icon="list-alt" className="mr-2 text-primary" />
              Autos de Infração
              <small className="text-muted ml-2" style={{ fontSize: '14px' }}>
                Receita Estadual
              </small>
            </h4>
            <hr style={{ borderTop: '2px solid #3f51b5' }} />
          </MDBCol>
        </MDBRow>

        {/* Cards de Estatísticas */}
        {estatisticas && (
          <MDBRow className="mb-3 justify-content-center">
            <StatCard
              icon="database"
              label="Total de Registros"
              value={estatisticas.total_registros?.toLocaleString('pt-BR') || '0'}
              gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            />
            <StatCard
              icon="dollar-sign"
              label="Total Impostos"
              value={formatarValor(estatisticas.valores?.total_imposto)}
              gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
            />
            <StatCard
              icon="gavel"
              label="Total Multas"
              value={formatarValor(estatisticas.valores?.total_multa)}
              gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
            />
            <StatCard
              icon="exclamation-circle"
              label="Total Multa Formal"
              value={formatarValor(estatisticas.valores?.total_multa_formal)}
              gradient="linear-gradient(135deg, #f7971e 0%, #ffd200 100%)"
            />
            <StatCard
              icon="calculator"
              label="Total Geral"
              value={formatarValor(estatisticas.valores?.total_geral)}
              gradient="linear-gradient(135deg, #fc5c7d 0%, #6a82fb 100%)"
            />
          </MDBRow>
        )}

        {/* Tabela Principal */}
        <MDBRow>
          <MDBCol md="12">
            <MDBCard style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: '8px' }}>
              <MDBCardBody>
                {/* Barra de Ações */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <MDBBtn
                      outline
                      color="primary"
                      size="sm"
                      onClick={() => setMostrarFiltros(!mostrarFiltros)}
                    >
                      <MDBIcon icon="filter" className="mr-1" />
                      {mostrarFiltros ? 'Ocultar Filtros' : 'Filtros'}
                    </MDBBtn>
                    <MDBBtn
                      outline
                      color="success"
                      size="sm"
                      onClick={exportarCSV}
                      disabled={loading || registros.length === 0}
                    >
                      <MDBIcon icon="file-excel" className="mr-1" />
                      Exportar
                    </MDBBtn>
                    <MDBBtn
                      outline
                      color="secondary"
                      size="sm"
                      onClick={() => history.replace({ pathname: location.pathname, search: location.search, state: { t: Date.now() } })}
                    >
                      <MDBIcon icon="refresh" className="mr-1" />
                      Atualizar
                    </MDBBtn>
                  </div>
                  <Link to="/dados-estatisticos/importar-autos">
                    <MDBBtn color="info" size="sm">
                      <MDBIcon icon="upload" className="mr-1" />
                      Importar
                    </MDBBtn>
                  </Link>
                </div>

                {/* Filtros */}
                <MDBCollapse isOpen={mostrarFiltros}>
                  <div
                    className="p-3 mb-3"
                    style={{
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef',
                    }}
                  >
                    <MDBRow>
                      <MDBCol md="3">
                        <label style={{ fontSize: '13px', fontWeight: 600 }}>Data Início</label>
                        <MDBInput type="date" value={rascunho.data_inicio} onChange={setCampo('data_inicio')} />
                      </MDBCol>
                      <MDBCol md="3">
                        <label style={{ fontSize: '13px', fontWeight: 600 }}>Data Final</label>
                        <MDBInput type="date" value={rascunho.data_final} onChange={setCampo('data_final')} />
                      </MDBCol>
                      <MDBCol md="3">
                        <label style={{ fontSize: '13px', fontWeight: 600 }}>Nº AI</label>
                        <MDBInput type="text" inputMode="numeric" value={rascunho.nu_ai} onChange={setNumero('nu_ai')} placeholder="Número do AI" />
                      </MDBCol>
                      <MDBCol md="3">
                        <label style={{ fontSize: '13px', fontWeight: 600 }}>Nº Infração</label>
                        <MDBInput type="text" inputMode="numeric" value={rascunho.nu_infracao} onChange={setNumero('nu_infracao')} placeholder="Ex: 1810" />
                      </MDBCol>
                    </MDBRow>
                    <MDBRow className="mt-2">
                      <MDBCol md="3">
                        <label style={{ fontSize: '13px', fontWeight: 600 }}>Status AI</label>
                        <select className="browser-default custom-select" value={rascunho.status_ai} onChange={setCampo('status_ai')}>
                          <option value="">Todos</option>
                          {opcoes.status_ai.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </MDBCol>
                      <MDBCol md="3">
                        <label style={{ fontSize: '13px', fontWeight: 600 }}>Acompanhamento</label>
                        <select className="browser-default custom-select" value={rascunho.status_acompanhamento} onChange={setCampo('status_acompanhamento')}>
                          <option value="">Todos</option>
                          {opcoes.status_acompanhamento.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </MDBCol>
                      <MDBCol md="3">
                        <label style={{ fontSize: '13px', fontWeight: 600 }}>Posto</label>
                        <select className="browser-default custom-select" value={rascunho.posto} onChange={setCampo('posto')}>
                          <option value="">Todos</option>
                          {opcoes.postos.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </MDBCol>
                      <MDBCol md="3" className="d-flex align-items-end pb-2">
                        <MDBBtn color="primary" size="sm" onClick={() => aplicar(rascunho)}>
                          <MDBIcon icon="search" className="mr-1" />
                          Buscar
                        </MDBBtn>
                        <MDBBtn outline color="secondary" size="sm" onClick={() => aplicar(FILTROS_VAZIOS)}>
                          <MDBIcon icon="eraser" className="mr-1" />
                          Limpar
                        </MDBBtn>
                      </MDBCol>
                    </MDBRow>
                  </div>
                </MDBCollapse>

                {/* Filtro aplicado que o formulário não mostra (#394): RF vindo de
                    link, ou status que não está nas opções. */}
                {foraDoFormulario.length > 0 && (
                  <div className="mb-2 d-flex flex-wrap" style={{ gap: '.5rem' }}>
                    {foraDoFormulario.map((f) => (
                      <span key={f.chave} className="badge badge-pill badge-light border" style={{ fontSize: '12px', fontWeight: 500 }}>
                        {f.rotulo}: {f.valor}
                        <button
                          type="button"
                          className="close ml-2"
                          style={{ fontSize: '14px', lineHeight: 1, float: 'none' }}
                          aria-label={`Remover filtro ${f.rotulo}`}
                          onClick={() => aplicar({ ...filtros, [f.chave]: '' })}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Info paginação */}
                <div className="mb-2 d-flex justify-content-between align-items-center">
                  <span className="text-muted" style={{ fontSize: '13px' }}>
                    Mostrando <strong>{registros.length}</strong> de <strong>{totalRegistros.toLocaleString('pt-BR')}</strong> registros
                  </span>
                  {totalPaginas > 0 && (
                    <span className="text-muted" style={{ fontSize: '13px' }}>
                      Página <strong>{paginaAtual}</strong> de <strong>{totalPaginas}</strong>
                    </span>
                  )}
                </div>

                {/* Tabela */}
                {loading ? (
                  <div className="text-center p-5">
                    <div
                      className="spinner-border text-primary"
                      role="status"
                      style={{ width: '3rem', height: '3rem' }}
                    >
                      <span className="sr-only">Carregando...</span>
                    </div>
                    <p className="mt-3 text-muted" style={{ fontSize: '14px' }}>
                      Carregando registros...
                    </p>
                  </div>
                ) : registros.length === 0 ? (
                  <div
                    className="text-center p-5"
                    style={{
                      backgroundColor: '#fff3e0',
                      borderRadius: '8px',
                    }}
                  >
                    <MDBIcon icon="search" size="3x" className="text-warning mb-3" />
                    <p className="mb-1 font-weight-bold">
                      {erroBusca ? 'Não foi possível buscar' : 'Nenhum registro encontrado'}
                    </p>
                    <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
                      {erroBusca || 'Tente ajustar os filtros ou importe um arquivo CSV.'}
                    </p>
                  </div>
                ) : (
                  <>
                    <div style={{ overflowX: 'auto' }}>
                      <MDBTable striped hover responsive small>
                        <MDBTableHead style={{ backgroundColor: '#f5f6fa' }}>
                          <tr>
                            <th style={{ fontSize: '12px', fontWeight: 700 }}>Nº AI</th>
                            <th style={{ fontSize: '12px', fontWeight: 700 }}>Data</th>
                            <th style={{ fontSize: '12px', fontWeight: 700 }}>Status AI</th>
                            <th style={{ fontSize: '12px', fontWeight: 700 }}>Acompanhamento</th>
                            <th style={{ fontSize: '12px', fontWeight: 700 }}>Nº Infração</th>
                            <th style={{ fontSize: '12px', fontWeight: 700 }}>Infração</th>
                            <th style={{ fontSize: '12px', fontWeight: 700 }}>RF</th>
                            <th style={{ fontSize: '12px', fontWeight: 700, textAlign: 'right' }}>Imposto</th>
                            <th style={{ fontSize: '12px', fontWeight: 700, textAlign: 'right' }}>Multa</th>
                            <th style={{ fontSize: '12px', fontWeight: 700, textAlign: 'right' }}>Multa Formal</th>
                            <th style={{ fontSize: '12px', fontWeight: 700, textAlign: 'right' }}>Total</th>
                          </tr>
                        </MDBTableHead>
                        <MDBTableBody>
                          {registros.map((registro) => (
                            <tr key={registro.id}>
                              <td style={{ fontSize: '12px', fontWeight: 600 }}>{registro.nu_ai}</td>
                              <td style={{ fontSize: '12px' }}>{formatarData(registro.dt_lavratura)}</td>
                              <td>
                                <BadgeStatus valor={registro.status_ai} tipo="status_ai" />
                              </td>
                              <td>
                                <BadgeStatus valor={registro.status_acompanhamento} tipo="acompanhamento" />
                              </td>
                              <td style={{ fontSize: '12px' }}>{registro.nu_infracao}</td>
                              <td style={{ fontSize: '11px', maxWidth: '200px' }} title={registro.infracao}>
                                {registro.infracao?.substring(0, 45)}
                                {registro.infracao?.length > 45 ? '...' : ''}
                              </td>
                              <td style={{ fontSize: '12px' }}>{registro.rf_lavratura}</td>
                              <td style={{ fontSize: '12px', textAlign: 'right' }}>{formatarValor(registro.imposto)}</td>
                              <td style={{ fontSize: '12px', textAlign: 'right' }}>{formatarValor(registro.multa)}</td>
                              <td style={{ fontSize: '12px', textAlign: 'right' }}>{formatarValor(registro.multa_formal)}</td>
                              <td style={{ fontSize: '12px', textAlign: 'right', fontWeight: 600 }}>
                                {formatarValor(registro.total)}
                              </td>
                            </tr>
                          ))}
                        </MDBTableBody>
                      </MDBTable>
                    </div>

                    {/* Paginação */}
                    {totalPaginas > 1 && (
                      <div className="d-flex justify-content-center mt-3">
                        <MDBPagination>
                          <MDBPageItem disabled={paginaAtual === 1}>
                            <MDBPageNav onClick={() => irParaPagina(1)}>
                              <MDBIcon icon="angle-double-left" />
                            </MDBPageNav>
                          </MDBPageItem>
                          <MDBPageItem disabled={paginaAtual === 1}>
                            <MDBPageNav onClick={() => irParaPagina(paginaAtual - 1)}>
                              <MDBIcon icon="angle-left" />
                            </MDBPageNav>
                          </MDBPageItem>

                          {[...Array(Math.min(totalPaginas, 5))].map((_, i) => {
                            let pageNum;
                            if (totalPaginas <= 5) {
                              pageNum = i + 1;
                            } else if (paginaAtual <= 3) {
                              pageNum = i + 1;
                            } else if (paginaAtual >= totalPaginas - 2) {
                              pageNum = totalPaginas - 4 + i;
                            } else {
                              pageNum = paginaAtual - 2 + i;
                            }
                            return (
                              <MDBPageItem key={pageNum} active={paginaAtual === pageNum}>
                                <MDBPageNav onClick={() => irParaPagina(pageNum)}>
                                  {pageNum}
                                </MDBPageNav>
                              </MDBPageItem>
                            );
                          })}

                          <MDBPageItem disabled={paginaAtual === totalPaginas}>
                            <MDBPageNav onClick={() => irParaPagina(paginaAtual + 1)}>
                              <MDBIcon icon="angle-right" />
                            </MDBPageNav>
                          </MDBPageItem>
                          <MDBPageItem disabled={paginaAtual === totalPaginas}>
                            <MDBPageNav onClick={() => irParaPagina(totalPaginas)}>
                              <MDBIcon icon="angle-double-right" />
                            </MDBPageNav>
                          </MDBPageItem>
                        </MDBPagination>
                      </div>
                    )}
                  </>
                )}
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>
      </MDBContainer>
    </>
  );
}
